import React, { useState, useEffect, useCallback } from 'react';
import { Staff } from '../../types';
import { X, Plus, Shield, Users, GraduationCap, Settings, DollarSign, Eye, EyeOff, AlertCircle, CheckCircle, CreditCard, Loader, RefreshCw, Upload, Image as ImageIcon } from 'lucide-react';
import { AUTHORITIES, ROLE_TEMPLATES, UserRole, getAuthoritiesByCategory } from '../../types/authorities';
import { useToast } from '../../contexts/ToastContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { startCardRegistration, cancelCardRegistration } from '../../services/rfidService';
import { uploadStaffImage, deleteStaffImage } from '../../services';

interface StaffFormProps {
    staff?: Staff;
    onSubmit: (staff: Omit<Staff, 'id' | 'createdAt' | 'updatedAt'>) => void;
    onCancel: () => void;
    loading?: boolean;
}

const StaffForm: React.FC<StaffFormProps> = ({ staff, onSubmit, onCancel, loading = false }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [checkingStaffId, setCheckingStaffId] = useState(false);
    const [staffIdError, setStaffIdError] = useState('');
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState(1);
    const { showError, showSuccess } = useToast();

    const [formData, setFormData] = useState({
        staffId: staff?.staffId || '',
        firstName: staff?.firstName || '',
        lastName: staff?.lastName || '',
        email: staff?.email || '',
        phone: staff?.phone || '',
        dateOfBirth: staff?.dateOfBirth ? (staff.dateOfBirth instanceof Date ? staff.dateOfBirth.toISOString().split('T')[0] : new Date(staff.dateOfBirth).toISOString().split('T')[0]) : '',
        gender: staff?.gender || 'male',
        cnic: staff?.cnic || '',
        profileImage: staff?.profileImage || '',
        selectedImageFile: null as File | null,
        password: '',
        confirmPassword: '',
        role: (staff?.role || 'clerk') as UserRole,
        address: {
            street: staff?.address?.street || '',
            city: staff?.address?.city || '',
            state: staff?.address?.state || '',
            zipCode: staff?.address?.zipCode || '',
            country: staff?.address?.country || '',
        },
        qualification: {
            education: staff?.qualification?.education || '',
            certifications: staff?.qualification?.certifications || [],
        },
        experience: {
            totalYears: staff?.experience?.totalYears || 0,
            previousEmployers: staff?.experience?.previousEmployers || [],
        },
        currentPosition: {
            department: staff?.currentPosition?.department || '',
            designation: staff?.currentPosition?.designation || '',
            joiningDate: staff?.currentPosition?.joiningDate ? (staff.currentPosition.joiningDate instanceof Date ? staff.currentPosition.joiningDate.toISOString().split('T')[0] : new Date(staff.currentPosition.joiningDate).toISOString().split('T')[0]) : '',
            salary: staff?.currentPosition?.salary || 0,
            status: staff?.currentPosition?.status || 'active',
        },
        emergencyContact: {
            name: staff?.emergencyContact?.name || '',
            relationship: staff?.emergencyContact?.relationship || '',
            phone: staff?.emergencyContact?.phone || '',
            email: staff?.emergencyContact?.email || '',
        },
        bankDetails: {
            accountNumber: staff?.bankDetails?.accountNumber || '',
            bankName: staff?.bankDetails?.bankName || '',
            ifscCode: staff?.bankDetails?.ifscCode || '',
            accountHolderName: staff?.bankDetails?.accountHolderName || '',
        },
        authorities: staff?.authorities || {},
        cardNumber: staff?.cardNumber || '',
    });

    // Load draft if creating new staff
    useEffect(() => {
        if (!staff) {
            const savedDraft = localStorage.getItem('staff_form_draft');
            if (savedDraft) {
                try {
                    const parsed = JSON.parse(savedDraft);
                    setFormData(prev => ({
                        ...prev,
                        ...parsed,
                        password: '',
                        confirmPassword: '',
                        selectedImageFile: null
                    }));
                    if (parsed.profileImage) {
                        setImagePreview(parsed.profileImage);
                    }
                    showSuccess('Draft Loaded', 'Your unsaved staff form draft has been loaded.');
                } catch (e) {
                    console.error('Error parsing draft:', e);
                }
            }
        }
    }, [staff]);

    // Save draft in local storage when formData changes
    useEffect(() => {
        if (!staff) {
            const { password, confirmPassword, selectedImageFile, ...draftData } = formData;
            const isDirty = Object.entries(draftData).some(([key, val]) => {
                if (typeof val === 'string') return val.trim() !== '';
                if (typeof val === 'object' && val !== null) {
                    return Object.values(val).some(innerVal => {
                        if (typeof innerVal === 'string') return innerVal.trim() !== '';
                        if (typeof innerVal === 'number') return innerVal !== 0;
                        return false;
                    });
                }
                return false;
            });

            if (isDirty) {
                localStorage.setItem('staff_form_draft', JSON.stringify(draftData));
            }
        }
    }, [formData, staff]);

    const [newCertification, setNewCertification] = useState('');
    const [staffIdValid, setStaffIdValid] = useState(false);
    const [emailValid, setEmailValid] = useState(false);

    // RFID Card Registration States
    const [isRegisteringCard, setIsRegisteringCard] = useState(false);
    const [cardRegistrationStatus, setCardRegistrationStatus] = useState<'idle' | 'waiting' | 'success' | 'error'>('idle');
    const [cardRegistrationError, setCardRegistrationError] = useState('');

    const authoritiesByCategory = getAuthoritiesByCategory(AUTHORITIES);

    // Auto-generate unique email
    const autoGenerateEmail = useCallback(async (firstNameVal: string, lastNameVal: string) => {
        const cleanFirst = firstNameVal.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
        const cleanLast = lastNameVal.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

        if (!cleanFirst) return;

        setCheckingEmail(true);
        try {
            const staffRef = collection(db, 'staff');
            let isUnique = false;
            let candidate = '';
            let attempts = 0;

            while (!isUnique && attempts < 30) {
                attempts++;
                const random3Digits = Math.floor(100 + Math.random() * 900);
                candidate = `${cleanFirst}${cleanLast}${random3Digits}@gmail.com`;

                const q = query(staffRef, where('email', '==', candidate));
                const snap = await getDocs(q);
                if (snap.empty) {
                    isUnique = true;
                }
            }

            if (isUnique && candidate) {
                setFormData(prev => ({
                    ...prev,
                    email: candidate
                }));
                setEmailError('');
                setEmailValid(true);
            }
        } catch (err) {
            console.error('Error generating unique email:', err);
        } finally {
            setCheckingEmail(false);
        }
    }, []);

    // Auto-validate Staff ID when user types (with debounce)
    useEffect(() => {
        if (!staff && formData.staffId.trim()) {
            const timer = setTimeout(() => {
                checkStaffIdUnique(formData.staffId);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [formData.staffId]);

    // Auto-validate / Generate Email when user types first/last name
    useEffect(() => {
        if (!staff && formData.firstName.trim()) {
            const timer = setTimeout(() => {
                autoGenerateEmail(formData.firstName, formData.lastName);
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [formData.firstName, formData.lastName, staff, autoGenerateEmail]);

    // Auto-set password when First Name changes for new staff
    useEffect(() => {
        if (!staff && formData.firstName.trim()) {
            const clean = formData.firstName.trim();
            const capitalized = clean.charAt(0).toUpperCase() + clean.slice(1);
            const autoPassword = `${capitalized}123!`;
            setFormData(prev => ({
                ...prev,
                password: autoPassword,
                confirmPassword: autoPassword
            }));
        }
    }, [formData.firstName, staff]);

    // Store selected image file
    const handleImageUpload = (file: File) => {
        setFormData(prev => ({
            ...prev,
            selectedImageFile: file
        }));

        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
        showSuccess('Success', 'Image selected successfully! Will be uploaded when you submit the form.');
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                showError('Invalid File', 'Please select an image file.');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                showError('File Too Large', 'Please select an image smaller than 5MB.');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);

            handleImageUpload(file);
        }
    };

    // Check if staff ID already exists
    const checkStaffIdUnique = async (staffId: string) => {
        if (!staffId.trim()) {
            setStaffIdError('');
            return true;
        }

        if (staff && staff.staffId === staffId) {
            setStaffIdError('');
            return true;
        }

        try {
            setCheckingStaffId(true);
            const staffRef = collection(db, 'staff');
            const q = query(staffRef, where('staffId', '==', staffId.trim()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setStaffIdError('This Staff ID already exists. Please use a unique ID.');
                setStaffIdValid(false);
                return false;
            } else {
                setStaffIdError('');
                setStaffIdValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking staff ID:', error);
            setStaffIdError('Error validating Staff ID. Please try again.');
            setStaffIdValid(false);
            return false;
        } finally {
            setCheckingStaffId(false);
        }
    };

    // Check if email already exists
    const checkEmailUnique = async (email: string) => {
        if (!email.trim()) {
            setEmailError('');
            return true;
        }

        if (staff && staff.email === email) {
            setEmailError('');
            return true;
        }

        try {
            setCheckingEmail(true);
            const staffRef = collection(db, 'staff');
            const q = query(staffRef, where('email', '==', email.trim().toLowerCase()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setEmailError('This email is already registered. Please use a different email.');
                setEmailValid(false);
                return false;
            } else {
                setEmailError('');
                setEmailValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking email:', error);
            setEmailError('Error validating email. Please try again.');
            setEmailValid(false);
            return false;
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormError(null);

        if (name === 'staffId') {
            setStaffIdError('');
            setStaffIdValid(false);
        }
        if (name === 'email') {
            setEmailError('');
            setEmailValid(false);
        }

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...(prev[parent as keyof typeof prev] as any),
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => {
                const nextData = {
                    ...prev,
                    [name]: value
                };

                // Auto-set password when editing First Name for new staff
                if (!staff && name === 'firstName') {
                    const clean = value.trim();
                    if (clean) {
                        const capitalized = clean.charAt(0).toUpperCase() + clean.slice(1);
                        const autoPassword = `${capitalized}123!`;
                        nextData.password = autoPassword;
                        nextData.confirmPassword = autoPassword;
                    }
                }

                return nextData;
            });
        }
    };

    const handleRoleChange = (role: UserRole) => {
        setFormData(prev => ({
            ...prev,
            role,
            authorities: ROLE_TEMPLATES[role].reduce((acc, authority) => {
                acc[authority] = true;
                return acc;
            }, {} as { [key: string]: boolean })
        }));
    };

    const handleAuthorityChange = (authorityId: string, checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            authorities: {
                ...prev.authorities,
                [authorityId]: checked
            }
        }));
    };

    const addCertification = () => {
        if (newCertification.trim()) {
            setFormData(prev => ({
                ...prev,
                qualification: {
                    ...prev.qualification,
                    certifications: [...prev.qualification.certifications, newCertification.trim()]
                }
            }));
            setNewCertification('');
        }
    };

    const removeCertification = (index: number) => {
        setFormData(prev => ({
            ...prev,
            qualification: {
                ...prev.qualification,
                certifications: prev.qualification.certifications.filter((_, i) => i !== index)
            }
        }));
    };

    const checkCardNumberExists = async (cardNumber: string): Promise<boolean> => {
        try {
            const staffRef = collection(db, 'staff');
            const staffQuery = query(staffRef, where('cardNumber', '==', cardNumber));
            const staffSnapshot = await getDocs(staffQuery);

            const teachersRef = collection(db, 'teachers');
            const teachersQuery = query(teachersRef, where('cardNumber', '==', cardNumber));
            const teachersSnapshot = await getDocs(teachersQuery);

            if (staff) {
                const staffDocs = staffSnapshot.docs.filter(doc => doc.id !== staff.id);
                return staffDocs.length > 0 || !teachersSnapshot.empty;
            }

            return !staffSnapshot.empty || !teachersSnapshot.empty;
        } catch (error) {
            console.error('Error checking card number:', error);
            return false;
        }
    };

    const handleRegisterCard = async () => {
        setIsRegisteringCard(true);
        setCardRegistrationStatus('waiting');
        setCardRegistrationError('');

        try {
            const cardNumber = await startCardRegistration();
            const cardExists = await checkCardNumberExists(cardNumber);

            if (cardExists) {
                setCardRegistrationError('This card is already registered to another user.');
                setCardRegistrationStatus('error');
                setIsRegisteringCard(false);
                showError('Card Already Registered', 'This RFID card is already registered to another user. Please use a different card.');
                return;
            }

            setFormData(prev => ({
                ...prev,
                cardNumber: cardNumber
            }));
            setCardRegistrationStatus('success');
            showSuccess('Card Registered', `RFID card ${cardNumber} has been successfully registered.`);
        } catch (error: any) {
            console.error('Error registering card:', error);
            setCardRegistrationError(error.message || 'Failed to register card. Please try again.');
            setCardRegistrationStatus('error');
            showError('Card Registration Failed', error.message || 'Failed to register card. Please check ESP32 connection and try again.');
        } finally {
            setIsRegisteringCard(false);
            setTimeout(() => {
                setCardRegistrationStatus('idle');
                setCardRegistrationError('');
            }, 3000);
        }
    };

    const handleCancelCardRegistration = async () => {
        try {
            await cancelCardRegistration();
            setIsRegisteringCard(false);
            setCardRegistrationStatus('idle');
            setCardRegistrationError('');
        } catch (error) {
            console.error('Error canceling card registration:', error);
        }
    };

    useEffect(() => {
        return () => {
            if (isRegisteringCard) {
                cancelCardRegistration();
            }
        };
    }, [isRegisteringCard]);

    const validateStep = (stepNum: number): boolean => {
        if (stepNum === 1) {
            if (!formData.firstName.trim()) {
                showError('First Name Required', 'Please enter First Name.');
                return false;
            }
            if (!formData.email.trim()) {
                showError('Email Required', 'Please enter an email address.');
                return false;
            }
            if (emailError) {
                showError('Duplicate Email', emailError);
                return false;
            }
            if (!staff) {
                if (!formData.staffId.trim()) {
                    showError('Staff ID Required', 'Please enter a Staff ID.');
                    return false;
                }
                if (staffIdError) {
                    showError('Duplicate Staff ID', staffIdError);
                    return false;
                }
                if (!formData.password || formData.password.length < 6) {
                    showError('Password Too Short', 'Password must be at least 6 characters long.');
                    return false;
                }
                if (formData.password !== formData.confirmPassword) {
                    showError('Passwords Mismatch', 'Passwords do not match.');
                    return false;
                }
            }
        }
        if (stepNum === 2) {
            if (!formData.role) {
                showError('Role Required', 'Please select a Role.');
                return false;
            }
        }
        if (stepNum === 3) {
            const salaryVal = Number(formData.currentPosition.salary);
            if (formData.currentPosition.salary === undefined || formData.currentPosition.salary === null || isNaN(salaryVal) || salaryVal <= 0) {
                showError('Salary Required', 'Please enter a valid Salary.');
                return false;
            }
        }
        return true;
    };

    const handleStepClick = (targetStep: number) => {
        if (targetStep < currentStep) {
            setCurrentStep(targetStep);
            return;
        }
        for (let s = currentStep; s < targetStep; s++) {
            if (!validateStep(s)) return;
        }
        setCurrentStep(targetStep);
    };

    const handleResetForm = () => {
        if (window.confirm('Are you sure you want to reset the form and clear the draft?')) {
            localStorage.removeItem('staff_form_draft');
            setImagePreview(null);
            setFormData({
                staffId: '',
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                dateOfBirth: '',
                gender: 'male',
                cnic: '',
                profileImage: '',
                selectedImageFile: null,
                password: '',
                confirmPassword: '',
                role: 'clerk',
                address: {
                    street: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    country: '',
                },
                qualification: {
                    education: '',
                    certifications: [],
                },
                experience: {
                    totalYears: 0,
                    previousEmployers: [],
                },
                currentPosition: {
                    department: '',
                    designation: '',
                    joiningDate: '',
                    salary: 0,
                    status: 'active',
                },
                emergencyContact: {
                    name: '',
                    relationship: '',
                    phone: '',
                    email: '',
                },
                bankDetails: {
                    accountNumber: '',
                    bankName: '',
                    ifscCode: '',
                    accountHolderName: '',
                },
                authorities: {},
                cardNumber: '',
            });
            setCurrentStep(1);
            showSuccess('Form Reset', 'The form has been reset and the draft has been cleared.');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);

        // Required field validations
        if (!formData.staffId.trim()) {
            const msg = 'Please enter a Staff ID.';
            setFormError(msg);
            showError('Staff ID Required', msg);
            return;
        }

        const isStaffIdUnique = await checkStaffIdUnique(formData.staffId);
        if (!isStaffIdUnique) {
            const msg = 'This Staff ID already exists. Please use a unique ID.';
            setFormError(msg);
            showError('Duplicate Staff ID', msg);
            return;
        }

        if (!formData.firstName.trim()) {
            const msg = 'Please enter First Name.';
            setFormError(msg);
            showError('First Name Required', msg);
            return;
        }

        if (!formData.email.trim()) {
            const msg = 'Please enter an email address.';
            setFormError(msg);
            showError('Email Required', msg);
            return;
        }

        const isEmailUnique = await checkEmailUnique(formData.email);
        if (!isEmailUnique) {
            const msg = 'This email is already registered. Please use a different email.';
            setFormError(msg);
            showError('Duplicate Email', msg);
            return;
        }

        if (!formData.role) {
            const msg = 'Please select a Role.';
            setFormError(msg);
            showError('Role Required', msg);
            return;
        }

        const salaryVal = Number(formData.currentPosition.salary);
        if (formData.currentPosition.salary === undefined || formData.currentPosition.salary === null || isNaN(salaryVal) || salaryVal <= 0) {
            const msg = 'Please enter a valid Salary.';
            setFormError(msg);
            showError('Salary Required', msg);
            return;
        }

        if (!staff) {
            if (!formData.password || formData.password.length < 6) {
                const msg = 'Password must be at least 6 characters long.';
                setFormError(msg);
                showError('Password Too Short', msg);
                return;
            }
        }

        const extractPublicId = (url: string): string | null => {
            const match = url.match(/\/v\d+\/(.+)\./);
            return match ? match[1] : null;
        };

        // Upload image if selected
        let profileImageUrl = formData.profileImage;
        if (formData.selectedImageFile) {
            setUploadingImage(true);
            try {
                if (staff && staff.profileImage) {
                    const oldPublicId = extractPublicId(staff.profileImage);
                    if (oldPublicId) {
                        try {
                            await deleteStaffImage(oldPublicId);
                        } catch (deleteError) {
                            console.warn('Failed to delete old image:', deleteError);
                        }
                    }
                }

                const result = await uploadStaffImage(formData.selectedImageFile, formData.staffId);
                profileImageUrl = result.secure_url;
            } catch (error) {
                console.error('Error uploading image:', error);
                showError('Error', 'Failed to upload image. Please try again.');
                setUploadingImage(false);
                return;
            }
            setUploadingImage(false);
        }

        const staffData = {
            ...formData,
            profileImage: profileImageUrl,
            dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : new Date(),
            currentPosition: {
                ...formData.currentPosition,
                joiningDate: formData.currentPosition.joiningDate ? new Date(formData.currentPosition.joiningDate) : new Date(),
                salary: salaryVal,
                status: formData.currentPosition.status || 'active',
            },
        };

        const { selectedImageFile, ...submittedData } = staffData;

        // Clear local storage draft
        localStorage.removeItem('staff_form_draft');

        onSubmit(submittedData);
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'management': return Shield;
            case 'academic': return GraduationCap;
            case 'financial': return DollarSign;
            case 'operations': return Settings;
            default: return Users;
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={onCancel}
            title={staff ? 'Edit Staff Member Details' : 'Add New Staff Member'}
            size="xl"
            footer={
                <>
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    {!staff && (
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={handleResetForm}
                            className="bg-red-50 hover:bg-red-100 text-red-600 border-red-200"
                        >
                            Reset Draft
                        </Button>
                    )}
                    {currentStep > 1 && (
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setCurrentStep(prev => prev - 1)}
                        >
                            Back
                        </Button>
                    )}
                    {currentStep < 4 ? (
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => handleStepClick(currentStep + 1)}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            type="submit"
                            form="staff-form"
                            size="sm"
                            loading={loading || uploadingImage || checkingStaffId || checkingEmail}
                            disabled={loading || uploadingImage || checkingStaffId || checkingEmail || !!staffIdError || !!emailError}
                        >
                            {loading ? 'Saving...' : staff ? 'Update Staff' : 'Add Staff'}
                        </Button>
                    )}
                </>
            }
        >
            {/* Loading Overlay */}
            {(loading || uploadingImage) && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-lg p-6 flex items-center space-x-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                        <div className="text-gray-700 font-medium">
                            {uploadingImage ? 'Uploading image...' : staff ? 'Updating staff...' : 'Creating staff account...'}
                        </div>
                    </div>
                </div>
            )}

            {/* Error Banner inside Modal */}
            {formError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3 text-red-800">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 text-sm font-medium">{formError}</div>
                    <button type="button" onClick={() => setFormError(null)} className="text-red-500 hover:text-red-700">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Stepper Header */}
            <div className="mb-6 bg-slate-50/60 p-4 rounded-2xl border border-slate-100/50 backdrop-blur-md">
                <div className="flex items-center justify-between max-w-3xl mx-auto">
                    {[
                        { step: 1, label: 'Basic Info' },
                        { step: 2, label: 'Role & Access' },
                        { step: 3, label: 'Professional' },
                        { step: 4, label: 'Contact & Bank' }
                    ].map((s, idx) => (
                        <React.Fragment key={s.step}>
                            {idx > 0 && (
                                <div className={`flex-1 h-0.5 mx-2 transition-all duration-500 ${
                                    currentStep > s.step ? 'bg-emerald-600' : 'bg-gray-200'
                                }`} />
                            )}
                            <button
                                type="button"
                                onClick={() => handleStepClick(s.step)}
                                className="flex flex-col items-center focus:outline-none group"
                            >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 ${
                                    currentStep === s.step
                                        ? 'bg-emerald-600 text-white ring-4 ring-emerald-50'
                                        : currentStep > s.step
                                            ? 'bg-emerald-100 text-emerald-800'
                                            : 'bg-gray-100 text-gray-500'
                                }`}>
                                    {s.step}
                                </div>
                                <span className={`text-[10px] font-bold mt-1 transition-colors ${
                                    currentStep === s.step ? 'text-emerald-700' : 'text-gray-400 group-hover:text-gray-600'
                                }`}>
                                    {s.label}
                                </span>
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <form id="staff-form" onSubmit={handleSubmit} autoComplete="off" className={`space-y-8 relative ${(loading || uploadingImage) ? 'pointer-events-none opacity-50' : ''}`}>
                {/* Step 1: Basic Info */}
                {currentStep === 1 && (
                    <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                        <Users className="h-6 w-6 mr-3 text-emerald-600" />
                        Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Staff ID */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Staff ID <span className="text-red-500">*</span>
                                </label>
                                {staff && (
                                    <span className="text-xs text-gray-400 font-normal">(Cannot be changed)</span>
                                )}
                            </div>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="staffId"
                                    value={formData.staffId}
                                    onChange={handleInputChange}
                                    placeholder="Enter unique staff ID"
                                    required
                                    readOnly={!!staff}
                                    disabled={checkingStaffId}
                                    autoComplete="off"
                                    className={`w-full px-3 py-2.5 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${staff
                                        ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                        : staffIdError
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                {!staff && checkingStaffId && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                    </div>
                                )}
                                {!staff && !checkingStaffId && staffIdError && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                )}
                                {!staff && !checkingStaffId && !staffIdError && staffIdValid && formData.staffId.trim() && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    </div>
                                )}
                            </div>
                            {!staff && staffIdError && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {staffIdError}
                                </p>
                            )}
                        </div>

                        {/* First Name */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    First Name <span className="text-red-500">*</span>
                                </label>
                            </div>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                placeholder="e.g. Arham"
                                required
                                autoComplete="off"
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>

                        {/* Last Name */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Last Name
                                </label>
                                <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                            </div>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                placeholder="e.g. Khan"
                                autoComplete="off"
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                {!staff ? (
                                    <button
                                        type="button"
                                        onClick={() => autoGenerateEmail(formData.firstName, formData.lastName)}
                                        disabled={checkingEmail || !formData.firstName.trim()}
                                        className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1 disabled:opacity-50 transition-colors"
                                    >
                                        <RefreshCw className={`h-3 w-3 ${checkingEmail ? 'animate-spin' : ''}`} />
                                        Generate New Email
                                    </button>
                                ) : (
                                    <span className="text-xs text-gray-400 font-normal">(Cannot be changed)</span>
                                )}
                            </div>
                            <div className="relative">
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Auto-generated email"
                                    required
                                    readOnly={!!staff}
                                    disabled={checkingEmail}
                                    autoComplete="off"
                                    className={`w-full px-3 py-2.5 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${staff
                                        ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                                        : emailError
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                {!staff && checkingEmail && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                    </div>
                                )}
                                {!staff && !checkingEmail && emailError && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                )}
                                {!staff && !checkingEmail && !emailError && emailValid && formData.email.trim() && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    </div>
                                )}
                            </div>
                            {!staff && emailError && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {emailError}
                                </p>
                            )}
                        </div>

                        {/* Phone */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Phone
                                </label>
                                <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                            </div>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                autoComplete="off"
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>

                        {/* Date of Birth */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Date of Birth
                                </label>
                                <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                            </div>
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                autoComplete="off"
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>

                        {/* Gender */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Gender
                                </label>
                                <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                            </div>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            >
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        {/* CNIC */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    CNIC
                                </label>
                                <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                            </div>
                            <input
                                type="text"
                                name="cnic"
                                value={formData.cnic}
                                onChange={handleInputChange}
                                placeholder="e.g. 12345-1234567-1"
                                autoComplete="off"
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>

                        {/* Password (Auto-set: e.g. Arham123!) */}
                        {!staff && (
                            <>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Password <span className="text-red-500">*</span>
                                        </label>
                                        <span className="text-xs text-emerald-600 font-semibold">
                                            Auto-set: {formData.firstName ? `${formData.firstName.trim().charAt(0).toUpperCase() + formData.firstName.trim().slice(1)}123!` : 'Arham123!'}
                                        </span>
                                    </div>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="Auto-set password"
                                            required={!staff}
                                            autoComplete="new-password"
                                            className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Confirm Password <span className="text-red-500">*</span>
                                        </label>
                                    </div>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            placeholder="Confirm password"
                                            required={!staff}
                                            autoComplete="new-password"
                                            className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Profile Image Upload */}
                        <div className="md:col-span-2 lg:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Image</label>
                            <div className="flex items-center space-x-4 bg-gray-50/50 p-4 rounded-xl border border-dashed border-gray-200">
                                <div className="flex-shrink-0">
                                    <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border">
                                        {imagePreview || formData.profileImage ? (
                                            <img
                                                src={imagePreview || formData.profileImage}
                                                alt="Profile Preview"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <ImageIcon className="h-6 w-6 text-gray-400" />
                                        )}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="hidden"
                                        id="profileImage"
                                    />
                                    <label
                                        htmlFor="profileImage"
                                        className="cursor-pointer inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-all font-medium"
                                    >
                                        {uploadingImage ? (
                                            <>
                                                <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="h-3.5 w-3.5 mr-1.5" />
                                                Upload Image
                                            </>
                                        )}
                                    </label>
                                    <p className="text-xs text-gray-400 mt-1">JPG, PNG or GIF (Max 5MB)</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Step 2: Role & Access */}
            {currentStep === 2 && (
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                        <Shield className="h-6 w-6 mr-3 text-emerald-600" />
                        Role & Permissions <span className="text-red-500 ml-1">*</span>
                    </h3>

                    {/* Role Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Role</label>
                        <p className="text-xs text-gray-500 mb-3">
                            Note: Teachers and Students have separate forms. Use this form for administrative and support staff only.
                        </p>
                        <select
                            value={formData.role}
                            onChange={(e) => handleRoleChange(e.target.value as UserRole)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300 bg-white capitalize font-medium text-gray-800"
                        >
                            {Object.keys(ROLE_TEMPLATES)
                                .filter(role => role !== 'teacher' && role !== 'student')
                                .map((role) => (
                                    <option key={role} value={role}>
                                        {role} ({ROLE_TEMPLATES[role as UserRole].length} permissions)
                                    </option>
                                ))}
                        </select>
                    </div>

                    {/* Detailed Permission Categories */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Customize System Permissions</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {Object.entries(authoritiesByCategory).map(([category, authorities]) => {
                                const CategoryIcon = getCategoryIcon(category);
                                return (
                                    <div key={category} className="space-y-3">
                                        <h4 className="font-medium text-gray-900 flex items-center">
                                            <CategoryIcon className="h-4 w-4 mr-2 text-emerald-600" />
                                            {category.charAt(0).toUpperCase() + category.slice(1)}
                                        </h4>
                                        <div className="space-y-2">
                                            {authorities.map((authority) => (
                                                <label key={authority.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.authorities[authority.id] || false}
                                                        onChange={(e) => handleAuthorityChange(authority.id, e.target.checked)}
                                                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium text-gray-900">{authority.name}</div>
                                                        <div className="text-xs text-gray-500">{authority.description}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Card>
            )}

            {/* Step 4: Contact & Bank */}
            {currentStep === 4 && (
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Address Information</h3>
                    <p className="text-sm text-gray-500 mb-6">Optional - Fill if address details are available</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Street Address
                            </label>
                            <input
                                type="text"
                                name="address.street"
                                value={formData.address.street}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                City
                            </label>
                            <input
                                type="text"
                                name="address.city"
                                value={formData.address.city}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                State
                            </label>
                            <input
                                type="text"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                ZIP Code
                            </label>
                            <input
                                type="text"
                                name="address.zipCode"
                                value={formData.address.zipCode}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Country
                            </label>
                            <input
                                type="text"
                                name="address.country"
                                value={formData.address.country}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                    </div>
                </Card>
            )}

            {/* Step 3: Professional Info */}
            {currentStep === 3 && (
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Professional Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Education <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                            </label>
                            <input
                                type="text"
                                name="qualification.education"
                                value={formData.qualification.education}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Total Experience (Years) <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                            </label>
                            <input
                                type="number"
                                name="experience.totalYears"
                                value={formData.experience.totalYears}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Department <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                            </label>
                            <input
                                type="text"
                                name="currentPosition.department"
                                value={formData.currentPosition.department}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Designation <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                            </label>
                            <input
                                type="text"
                                name="currentPosition.designation"
                                value={formData.currentPosition.designation}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Joining Date <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                            </label>
                            <input
                                type="date"
                                name="currentPosition.joiningDate"
                                value={formData.currentPosition.joiningDate}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Salary <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="currentPosition.salary"
                                value={formData.currentPosition.salary}
                                onChange={handleInputChange}
                                placeholder="Enter salary amount"
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-300"
                            />
                        </div>
                    </div>

                    {/* Certifications */}
                    <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Certifications <span className="text-xs text-gray-500 font-normal">(Optional)</span></label>
                        <div className="flex space-x-2 mb-3">
                            <input
                                type="text"
                                value={newCertification}
                                onChange={(e) => setNewCertification(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                placeholder="Add certification"
                            />
                            <Button type="button" onClick={addCertification} variant="secondary">
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {formData.qualification.certifications.map((cert, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 text-emerald-800"
                                >
                                    {cert}
                                    <button
                                        type="button"
                                        onClick={() => removeCertification(index)}
                                        className="ml-2 hover:text-emerald-600"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                </Card>
            )}

            {/* Emergency Contact (Step 4) */}
            {currentStep === 4 && (
                <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Emergency Contact</h3>
                    <p className="text-sm text-gray-500 mb-6">Optional - Can be added later</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
                            <input
                                type="text"
                                name="emergencyContact.name"
                                value={formData.emergencyContact.name}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                            <input
                                type="text"
                                name="emergencyContact.relationship"
                                value={formData.emergencyContact.relationship}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                            <input
                                type="text"
                                name="emergencyContact.phone"
                                value={formData.emergencyContact.phone}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                            <input
                                type="email"
                                name="emergencyContact.email"
                                value={formData.emergencyContact.email}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                </Card>
            )}

                {/* RFID Card Registration (Shown only when editing existing staff) */}
                {currentStep === 2 && staff && (
                    <Card className="p-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <CreditCard className="h-6 w-6 mr-3 text-emerald-600" />
                            RFID Card Registration
                        </h3>
                        <div className="space-y-4">
                            {formData.cardNumber ? (
                                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-green-900">Card Registered</p>
                                            <p className="text-sm text-green-700 mt-1">Card Number: <span className="font-mono font-semibold">{formData.cardNumber}</span></p>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleRegisterCard}
                                            disabled={isRegisteringCard}
                                            className="flex items-center space-x-2"
                                        >
                                            <CreditCard className="h-4 w-4" />
                                            <span>Change Card</span>
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                                    <p className="text-sm text-gray-700 mb-4">No RFID card registered. Click the button below to register a card.</p>
                                    <Button
                                        type="button"
                                        onClick={handleRegisterCard}
                                        disabled={isRegisteringCard}
                                        loading={isRegisteringCard}
                                        className="flex items-center space-x-2"
                                    >
                                        <CreditCard className="h-4 w-4" />
                                        <span>{isRegisteringCard ? 'Waiting for Card...' : 'Register Card'}</span>
                                    </Button>
                                </div>
                            )}

                            {cardRegistrationStatus === 'waiting' && (
                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                    <div className="flex items-center space-x-3">
                                        <Loader className="h-5 w-5 text-blue-600 animate-spin" />
                                        <div>
                                            <p className="text-sm font-medium text-blue-900">Waiting for Card...</p>
                                            <p className="text-xs text-blue-700 mt-1">Please swipe your RFID card on the reader</p>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            onClick={handleCancelCardRegistration}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {cardRegistrationStatus === 'error' && cardRegistrationError && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                                    <div className="flex items-center space-x-2">
                                        <AlertCircle className="h-5 w-5 text-red-600" />
                                        <p className="text-sm text-red-700">{cardRegistrationError}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                {/* Bank Details */}
                {currentStep === 4 && (
                    <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Bank Details</h3>
                    <p className="text-sm text-gray-500 mb-6">Optional - Can be added later</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                            <input
                                type="text"
                                name="bankDetails.accountNumber"
                                value={formData.bankDetails.accountNumber}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                            <input
                                type="text"
                                name="bankDetails.bankName"
                                value={formData.bankDetails.bankName}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                            <input
                                type="text"
                                name="bankDetails.ifscCode"
                                value={formData.bankDetails.ifscCode}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder Name</label>
                            <input
                                type="text"
                                name="bankDetails.accountHolderName"
                                value={formData.bankDetails.accountHolderName}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                </Card>
            )}

            </form>
        </Modal>
    );
};

export default StaffForm;